import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Turnstile } from "@marsidev/react-turnstile";
import VoiceConversation from "@/components/VoiceConversation";
import WaitlistPanel from "@/components/WaitlistPanel";
import OriginalRecordings from "@/components/OriginalRecordings";
import OriginalVideos from "@/components/OriginalVideos";
import type { Session } from "@supabase/supabase-js";
import { publicDateLabel } from "@/lib/tributeDates";

const TURNSTILE_SITE_KEY = import.meta.env.DEV
  ? '1x00000000000000000000AA'  // Cloudflare dummy key — always passes, works on localhost
  : '0x4AAAAAADBMGIs-tgOOjspq'; // Production key — restricted to legacylensai.com

type Tribute = {
  id: string;
  slug: string;
  full_name: string;
  date_of_birth: string | null;
  date_of_death: string | null;
  headline: string | null;
  short_tribute: string | null;
  cover_image_url: string | null;
  model_glb_url: string | null;
  model_usdz_url: string | null;
  voice_enabled: boolean;
};

const TributePage = () => {
  const { slug } = useParams();
  const modelViewerRef = useRef<HTMLElement>(null);
  const voiceContainerRef = useRef<HTMLDivElement>(null);
  const [tribute, setTribute] = useState<Tribute | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Anonymous auth state
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Load tribute data
  useEffect(() => {
    if (!slug) return;
    supabase
      .from("tributes")
      .select("id, slug, full_name, date_of_birth, date_of_death, headline, short_tribute, cover_image_url, model_glb_url, model_usdz_url, voice_enabled, is_published, created_at, status")
      .eq("slug", slug)
      .eq("is_published", true)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          setNotFound(true);
        } else {
          setTribute(data);
        }
        setLoading(false);
      });
  }, [slug]);

  // Check for existing session (persisted in localStorage from a previous visit)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // Turnstile success → anonymous sign-in
  const handleTurnstileSuccess = async (captchaToken: string) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInAnonymously({
      options: { captchaToken },
    });
    if (error) {
      console.error("[TributePage] Anonymous sign-in failed:", error);
      setAuthError("Verification failed. Please refresh and try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-10 mx-auto mb-4 opacity-60" />
          <p className="text-muted-foreground">Loading tribute...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-10 mx-auto mb-6 opacity-60" />
          <h1 className="font-display text-3xl font-bold text-foreground mb-3">Tribute Not Found</h1>
          <p className="text-muted-foreground mb-6">This tribute may not exist or isn't published yet.</p>
          <Link to="/" className="text-primary hover:underline text-sm">← Back to LegacyLens AI</Link>
        </div>
      </div>
    );
  }

  const years = publicDateLabel(tribute!.date_of_birth, tribute!.date_of_death);

  // Whether the visitor has been verified and can chat
  const canChat = !!session && !authLoading;

  return (
    <div className="min-h-screen bg-background">
      {/* Subtle branding */}
      <div className="px-4 py-3 flex justify-center">
        <Link to="/" className="opacity-50 hover:opacity-80 transition-opacity">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-7" />
        </Link>
      </div>

      <main className="max-w-2xl mx-auto px-4 pb-16">
        {/* Name & years */}
        <div className="text-center pt-6 pb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-3">
            {tribute!.full_name}
          </h1>
          {tribute!.headline && (
            <p className="text-muted-foreground italic mt-3 text-lg">{tribute!.headline}</p>
          )}
          {years && (
            <p className="text-lg text-ivory-muted font-display tracking-wider mt-2">{years}</p>
          )}
        </div>

        {/* Cover image */}
        {tribute!.cover_image_url && (
          <div className="mb-8 rounded-xl overflow-hidden border border-border/30">
            <img
              src={tribute!.cover_image_url}
              alt={tribute!.full_name}
              className="w-full h-auto object-cover max-h-[400px]"
            />
          </div>
        )}

        {/* Short tribute */}
        {tribute!.short_tribute && (
          <div className="mb-10 px-2">
            <p className="text-foreground/90 leading-relaxed text-base whitespace-pre-line">
              {tribute!.short_tribute}
            </p>
          </div>
        )}

        {/* 3D Model Viewer */}
        {tribute!.model_glb_url && (
          <div className="mb-12">
            <h2 className="font-display text-2xl font-semibold text-foreground text-center mb-4">
              Experience in 3D
            </h2>
            <div className="rounded-xl overflow-hidden border border-border/30 bg-card relative">
              {/* @ts-ignore */}
              <model-viewer
                ref={modelViewerRef}
                src={tribute!.model_glb_url}
                {...(tribute!.model_usdz_url ? { "ios-src": tribute!.model_usdz_url } : {})}
                poster={tribute!.cover_image_url || undefined}
                camera-controls
                auto-rotate
                ar
                ar-modes="webxr scene-viewer quick-look"
                shadow-intensity="1"
                style={{ width: "100%", height: "500px", minHeight: "280px", maxHeight: "720px" }}
              >
                <button
                  slot="ar-button"
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold text-sm shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  View in AR
                </button>
              </model-viewer>
            </div>
            <p className="text-center text-muted-foreground text-sm mt-3">
              Drag to rotate · Pinch to zoom · Meant for AR on mobile devices
            </p>

            {/* Voice Conversation — gated behind Turnstile + anonymous auth */}
            {tribute!.voice_enabled && (
              <div className="mt-8" ref={voiceContainerRef}>
                {canChat ? (
                  <VoiceConversation
                    tributeId={tribute!.id}
                    tributeName={tribute!.full_name}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <span className="text-xl font-medium">Have a Conversation</span>
                    {authLoading ? (
                      <p className="text-sm text-muted-foreground">Loading...</p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Verifying your browser...
                        </p>
                        <Turnstile
                          siteKey={TURNSTILE_SITE_KEY}
                          onSuccess={handleTurnstileSuccess}
                          onError={() =>
                            setAuthError("Verification failed. Please refresh and try again.")
                          }
                          onExpire={() =>
                            setAuthError("Verification expired. Please refresh and try again.")
                          }
                          options={{ theme: "dark" }}
                        />
                        {authError && (
                          <p className="text-sm text-destructive text-center max-w-xs">
                            {authError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Original recordings — last section. Self-gating: renders nothing
            unless the tribute-raw-audio seam returns enabled with clips. */}
        <OriginalRecordings slug={tribute!.slug} name={tribute!.full_name} />
        <OriginalVideos slug={tribute!.slug} name={tribute!.full_name} />

      </main>

      {tribute && (
        <WaitlistPanel
          slug={slug!}
          modelViewerRef={modelViewerRef}
          voiceContainerRef={voiceContainerRef}
        />
      )}

      {/* Footer */}
      <footer className="border-t border-border/30 py-6 text-center">
        <Link to="/" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
          Powered by <span className="text-primary">LegacyLens AI</span>
        </Link>
      </footer>
    </div>
  );
};

export default TributePage;
